/**
 * One row in a searchable select.
 *
 * The shape is deliberately flat and presentation-oriented rather than
 * domain-specific: a teacher, a school, a department and a subject all reduce
 * to the same four things — what it is worth, what to call it, what tells it
 * apart from a similar-looking neighbour, and whether it should be offered
 * with a caveat.
 */
export interface SelectOption<T = unknown> {
    /** What the form control receives when this row is chosen. */
    value: T;

    /** The primary line. A person's name, a subject, a school. */
    label: string;

    /**
     * The secondary line, rendered smaller under the label. Meant for the
     * details that disambiguate two rows with the same label — an employee
     * code, a department, a city. Also searched, so typing a department name
     * narrows to that department.
     */
    hint?: string;

    /**
     * A short badge on the right of the row: employment type, a status, a
     * count. Kept separate from `hint` because it is a category, not prose.
     */
    badge?: string;

    /**
     * Marks a row as offerable-but-questionable — a teacher who has left, a
     * deprecated master value. Rendered muted with a warning icon and sorted
     * after the clean rows, rather than hidden: existing records still point
     * at them, so removing them from the list would make those records
     * un-editable.
     */
    flagged?: boolean;

    /** Tooltip explaining the flag, e.g. "No longer on the teaching roll". */
    flagNote?: string;

    /** Greys the row out and blocks selection. */
    disabled?: boolean;
}

/**
 * Builds the searchable haystack for one option. Kept here so the search
 * behaviour is identical everywhere and cannot drift per screen.
 */
export function searchTextOf(option: SelectOption): string {
    return [option.label, option.hint, option.badge]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
}
